// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Grid, SxProps, Typography } from '@mui/material';

// ui
import { CopyToClipboard } from '../CopyToClipboard/CopyToClipboard';
import { IconCircle, IconCircleProps } from '../IconCircle/IconCircle';

// utils
import { truncateHash } from '../../../utils/strings';

// props
export interface BlockchainHashProps {
	isFullWidth?: boolean;
	hash: string | undefined;
	hasIntegrity?: boolean;
}

export const BlockchainHash = ({
	isFullWidth = false,
	hash,
	hasIntegrity,
}: BlockchainHashProps) => {
	// hooks
	const { t } = useTranslation();

	if (!hash) {
		return <Typography>{t('na')}</Typography>;
	}

	let text = hash;

	if (!isFullWidth) {
		text = truncateHash(hash);
	}

	let IconCircleProps: IconCircleProps | undefined;

	if (hasIntegrity === null) {
		IconCircleProps = {
			TooltipProps: {
				title: t('integrityUnknown'),
			},
			type: 'priority',
		};
	} else if (typeof hasIntegrity === 'boolean') {
		IconCircleProps = {
			TooltipProps: {
				title: t('integrityVerified'),
			},
			type: 'check',
		};

		if (!hasIntegrity) {
			IconCircleProps = {
				color: 'error',
				TooltipProps: {
					title: t('integrityUnverified'),
				},
				type: 'close',
			};
		}
	}

	return (
		<Box
			sx={(theme) => {
				let sx: SxProps = {
					backgroundColor: theme.palette.brand.grey[100],
					border: `1px ${theme.palette.brand.grey[300]} solid`,
					borderRadius: theme.spacing(0.5),
					display: 'inline-flex',
					padding: theme.spacing(0.5, 1),
				};

				if (isFullWidth) {
					sx = {
						...sx,
						width: '100%',
					};
				}
				return sx;
			}}>
			<Grid alignItems="center" container justifyContent="space-between" spacing={0.5}>
				{IconCircleProps && (
					<Grid item>
						<IconCircle size="xs" {...IconCircleProps} />
					</Grid>
				)}
				<Grid
					item
					sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
					xs={true}>
					<Typography variant="caption">{text}</Typography>
				</Grid>
				<Grid item xs="auto">
					<CopyToClipboard content={hash} />
				</Grid>
			</Grid>
		</Box>
	);
};
